import React, { Component } from 'react';
import { forEach, get, omit } from 'lodash-es';
import { connectToModel, bindToPath } from 'client/data/luckdragon/redux/react-binding';
/* Models */
import { FinancingModel, PATHS } from 'client/data/models/financing';
/* Constants */
import { EVENT_TYPES } from 'client/site-modules/financing/constants/financing';
import { ERROR_STATUS, FINANCING_RESPONSE_TYPES } from 'site-modules/shared/constants/financing/financing';
/* Utils */
import { getSelectedOffer } from 'site-modules/shared/utils/financing/financing';
import { setFinancingPrequalifiedCookie } from 'site-modules/shared/utils/financing/financing-prequalified-cookie';
import { isMockId } from 'site-modules/shared/utils/financing/mock-util';
/* Tracking */
import { EventToolbox } from 'client/utils/event-toolbox';
import { FINANCING_EVENTS } from 'site-modules/shared/constants/financing/financing-events';
import { getDecryptedFinancialFields, getDecryptedPrequalifyData } from 'site-modules/financing/utils/financing-utils';
import { getEncryptedFields } from 'site-modules/financing/utils/encrypting-utils';
/* HOC */
import { connectedInsiderSaveVinWrapper } from 'site-modules/shared/components/profile/insider-save-vin-wrapper/insider-save-vin-wrapper';

export function preQualify(WrappedComponent) {
  class PreQualify extends Component {
    state = {
      isOffersRecalculated: false,
    };
    componentDidMount() {
      this.addFinancingListener();
    }

    componentWillUnmount() {
      this.removeFinancingListener();
    }

    saveOffers = async offers => {
      const { setModelValue, vin, isAllowBypassApiGateway } = this.props;

      await setModelValue(PATHS.FINANCING_OFFERS({ vin, isAllowBypassApiGateway }), FinancingModel, offers);
    };

    savePrequalificationData = async (id, type) => {
      const decryptedPrequalifyData = await getDecryptedPrequalifyData();
      const data = await getDecryptedFinancialFields();
      const isExistingPrequalify = type === FINANCING_RESPONSE_TYPES.EXISTING_PREQUALIFY;
      const prequalificationData = await getEncryptedFields({
        ...decryptedPrequalifyData,
        id,
        firstName: isExistingPrequalify
          ? get(data, 'lookup.firstName', '')
          : get(data, 'personal.firstNamePrimary', ''),
        lastName: isExistingPrequalify ? get(data, 'lookup.lastName', '') : get(data, 'personal.lastNamePrimary', ''),
        email: isExistingPrequalify ? get(data, 'lookup.email') : get(data, 'personal.emailPrimary'),
        phoneNumber: isExistingPrequalify
          ? get(data, 'lookup.phoneNumber', '')
          : get(data, 'personal.phoneNumberPrimary', ''),
        zipCode: isExistingPrequalify ? get(data, 'lookup.zip', '') : get(data, 'residence.zipPrimary', ''),
      });

      setFinancingPrequalifiedCookie(JSON.stringify(prequalificationData));
    };

    saveToInsider = async event => {
      const { startInsiderAction, vehicle } = this.props;

      EventToolbox.fireCustomEvent(FINANCING_EVENTS.SAVE_VEHICLE, { vin: get(vehicle, 'vin') });
      await startInsiderAction(event);
    };

    /**
     * Listens financing update.
     * @param {string} data
     * @returns {Promise<void>}
     */
    listenForFinancing = async ({ data }) => {
      if (!data || data.type !== EVENT_TYPES.FINANCING_CHECK) {
        return;
      }

      if (typeof data.data === 'string') {
        const {
          redirectPage,
          showErrorPage,
          sendConsumerEmail,
          isMatchingVIN,
          setQualifiedStatus,
          debugFinancingAPI,
          vehicle,
          isPreVinFlow,
        } = this.props;

        const { financingResponse } = JSON.parse(String(data.data));
        const status = get(financingResponse, 'status');
        const type = get(financingResponse, 'type');

        if (debugFinancingAPI) {
          const payload = get(financingResponse, 'payload', []);
          const response = get(financingResponse, 'response', []);
          const apiCalls = get(financingResponse, 'api', []);

          forEach(apiCalls, (api, index) => {
            /* eslint-disable no-console */
            console.log(`DEBUG API ${api} - PAYLOAD: `, payload[index]);
            console.log(`DEBUG API ${api} - RESPONSE: `, response[index]);
            /* eslint-enable no-console */
          });
        }

        if (status === ERROR_STATUS.SYSTEM_ERROR) {
          EventToolbox.fireCustomEvent(FINANCING_EVENTS.API_SYSTEM_ERROR, {
            financingResponse,
          });
        }

        switch (type) {
          case FINANCING_RESPONSE_TYPES.PREQUALIFY:
          case FINANCING_RESPONSE_TYPES.EXISTING_PREQUALIFY: {
            const id = get(financingResponse, 'applicationId');
            if (id) {
              await this.savePrequalificationData(get(financingResponse, 'applicationId'), type);
            }

            if (
              type === FINANCING_RESPONSE_TYPES.PREQUALIFY &&
              [ERROR_STATUS.APPROVED, ERROR_STATUS.VEHICLE_NOT_FOUND].includes(status)
            ) {
              EventToolbox.fireCustomEvent(FINANCING_EVENTS.PREQUAL_NEWAPP_APPROVAL, {
                vehicle,
                isMatchingVIN,
                isPreVinFlow,
              });
            }

            if (status === ERROR_STATUS.APPROVED) {
              if (!isPreVinFlow) {
                if (isMatchingVIN) {
                  await this.saveOffers(get(financingResponse, 'offers'));
                }
                if (!isMockId(id)) {
                  await this.saveToInsider();
                  await sendConsumerEmail(status);
                }
              } else {
                await sendConsumerEmail(status);
              }

              redirectPage();
            } else {
              if (type === FINANCING_RESPONSE_TYPES.EXISTING_PREQUALIFY) {
                setQualifiedStatus(status);
              }

              await showErrorPage(status);
            }
            break;
          }
          case FINANCING_RESPONSE_TYPES.UPDATE: {
            if (status === ERROR_STATUS.APPROVED) {
              const offers = get(financingResponse, 'offers');
              await this.saveOffers(offers);
              const selectedOffer = getSelectedOffer(offers);

              EventToolbox.fireCustomEvent(FINANCING_EVENTS.CALCULATE_PAYMENTS, {
                monthlyPayment: get(selectedOffer, 'financeInformation.monthlyPayment'),
                selectedTerm: get(selectedOffer, 'financeInformation.loanTerm'),
              });

              EventToolbox.fireCustomEvent(FINANCING_EVENTS.PAYMENT_LIST_PIXEL, { offers });

              const decryptedPrequalifyData = await getDecryptedPrequalifyData();
              const prequalificationData = await getEncryptedFields(omit(decryptedPrequalifyData, ['updatePayments']));

              setFinancingPrequalifiedCookie(JSON.stringify(prequalificationData));

              this.setState({ isOffersRecalculated: true });
            } else {
              await showErrorPage(status);
            }

            break;
          }
          default: {
            break;
          }
        }
      }
    };

    addFinancingListener = () => {
      window.addEventListener('message', this.listenForFinancing, false);
    };

    removeFinancingListener = () => {
      window.removeEventListener('message', this.listenForFinancing, false);
    };

    render() {
      const { ...props } = this.props;
      return <WrappedComponent {...props} isOffersRecalculated={this.state.isOffersRecalculated} />;
    }
  }

  const wrappedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  PreQualify.displayName = `preQualify(${wrappedComponentName})`;

  return PreQualify;
}

export const stateToPropsConfig = {
  financingResponse: bindToPath(PATHS.FINANCING_RESPONSE, FinancingModel),
};

export const connectedPreQualify = WrappedComponent =>
  connectedInsiderSaveVinWrapper(connectToModel(preQualify(WrappedComponent), stateToPropsConfig), false);
